import { useState, useCallback } from 'react';
import {
  Button,
  Text,
  TextInput,
  Select,
  NumberInput,
  Switch,
} from '@cowbell/ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck } from '@fortawesome/pro-regular-svg-icons';
import { useActionExecution } from '../../hooks/useActionExecution';
import type { FormInputAction, AssistantMessage } from '../../types';
import styles from './ActionCard.module.css';

interface Props {
  action: FormInputAction;
  message: AssistantMessage;
}

export default function FormInputCard({ action, message }: Props) {
  const [values, setValues] = useState<Record<string, unknown>>(() => {
    const defaults: Record<string, unknown> = {};
    action.fields.forEach((field) => {
      if (field.default !== undefined) {
        defaults[field.id] = field.default;
      }
    });
    return defaults;
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { mutateAsync, isLoading } = useActionExecution();

  const handleSubmit = useCallback(async () => {
    setError(null);
    try {
      await mutateAsync({
        correlationId: message.correlationId,
        messageId: message.messageId,
        action: action.submitAction,
        payload: values,
      });
      setSubmitted(true);
    } catch {
      setError('Submission failed. Please try again.');
    }
  }, [mutateAsync, message, action, values]);

  if (submitted) {
    return (
      <div className={styles.confirmed}>
        <div className={styles.successMessage}>
          <FontAwesomeIcon icon={faCircleCheck} />
          {action.submitLabel} completed
        </div>
      </div>
    );
  }

  return (
    <div className={styles.card}>
      {action.title && <div className={styles.cardTitle}>{action.title}</div>}

      <div className={styles.formFields}>
        {action.fields.map((field) => {
          switch (field.type) {
            case 'text':
              return (
                <TextInput
                  key={field.id}
                  label={field.label}
                  required={field.required}
                  size="xs"
                  value={(values[field.id] as string) || ''}
                  onChange={(e) =>
                    setValues((prev) => ({
                      ...prev,
                      [field.id]: e.currentTarget.value,
                    }))
                  }
                />
              );
            case 'number':
              return (
                <NumberInput
                  key={field.id}
                  label={field.label}
                  required={field.required}
                  size="xs"
                  value={(values[field.id] as number) ?? ''}
                  onChange={(val) =>
                    setValues((prev) => ({ ...prev, [field.id]: val }))
                  }
                />
              );
            case 'select':
              return (
                <Select
                  key={field.id}
                  label={field.label}
                  required={field.required}
                  size="xs"
                  data={field.options || []}
                  value={(values[field.id] as string) || null}
                  onChange={(val) =>
                    setValues((prev) => ({ ...prev, [field.id]: val }))
                  }
                />
              );
            case 'toggle':
              return (
                <Switch
                  key={field.id}
                  label={field.label}
                  size="xs"
                  checked={!!values[field.id]}
                  onChange={(e) =>
                    setValues((prev) => ({
                      ...prev,
                      [field.id]: e.currentTarget.checked,
                    }))
                  }
                />
              );
            case 'date':
              return (
                <TextInput
                  key={field.id}
                  label={field.label}
                  required={field.required}
                  size="xs"
                  type="date"
                  value={(values[field.id] as string) || ''}
                  onChange={(e) =>
                    setValues((prev) => ({
                      ...prev,
                      [field.id]: e.currentTarget.value,
                    }))
                  }
                />
              );
            default:
              return null;
          }
        })}
      </div>

      {error && (
        <Text size="xs" c="red" mt={4}>
          {error}
        </Text>
      )}

      <div className={styles.buttonGroup}>
        <Button size="xs" onClick={handleSubmit} loading={isLoading}>
          {action.submitLabel}
        </Button>
      </div>
    </div>
  );
}
